import { CardConditionalHighlight, CardTitle } from "../common";
import React, { useImperativeHandle, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons";
import { faPlay } from "@fortawesome/free-solid-svg-icons";

const MessageEventCard = React.forwardRef(
  (
    {
      card,
      topic,
      messages,
      w,
      h,
      displayVariant,
      displayParameters,
      pipeline = false,
    },
    ref
  ) => {
    // Action handler: set the selected card
    const action = (setSelectedCard) => {
      setSelectedCard(card);
    };

    useImperativeHandle(ref, () => ({
      action,
    }));

    const [showCondition, setShowCondition] = useState(false);

    const renderMatch = () => {
      return (
        <div className="w-full h-full flex flex-col justify-around bg-seasalt">
          {h > 1 && <CardTitle title={topic.name} />}
          <div className="flex flex-col font-sans gap-2 justify-center">
            <div className="flex gap-1 flex-col">
              {h > 1 && (
                <p className="text-gray-600 font-sans text-sm">When:</p>
              )}
              <p className="text-gray-600 bg-ghost-white px-1 rounded-md font-sans font-semibold">
                {displayParameters.topic}
              </p>
            </div>
            <div className="flex gap-1 flex-col">
              {h > 1 && <p className="text-gray-600 font-sans text-sm">Run:</p>}
              <p className="text-gray-600 bg-ghost-white px-1 rounded-md font-sans font-semibold">
                {displayParameters.action}
              </p>
            </div>
          </div>
        </div>
      );
    };

    const renderScript = () => {
      return (
        <div className="w-full h-full flex flex-col justify-around bg-seasalt">
          {h > 1 && <CardTitle title={topic.name} />}
          <div className="flex flex-col font-sans gap-2">
            <div className="flex gap-1 flex-col">
              {h > 1 && (
                <p className="text-gray-600 font-sans text-sm">When:</p>
              )}
              <p className="text-gray-600 bg-ghost-white px-1 rounded-md font-sans font-semibold">
                {displayParameters.topic}
              </p>
            </div>
            <div className="flex gap-1 flex-col">
              {h > 1 && <p className="text-gray-600 font-sans text-sm">Run:</p>}
              <p className="text-gray-600 bg-ghost-white px-1 rounded-md font-sans font-semibold">
                {displayParameters.action}
              </p>
            </div>
          </div>
        </div>
      );
    };

    const renderContent = () => {
      switch (displayVariant) {
        case "match":
          return renderMatch();
        case "script":
          return renderScript();
        default:
          return <div>Invalid display variant</div>;
      }
    };

    const renderPipelineView = () => {
      const runCount = messages?.length || 0;
      const lastRun =
        runCount > 0
          ? new Date(messages[messages.length - 1].timestamp).toLocaleString()
          : null;

      return (
        <div className="w-full bg-green-50 border border-green-200 rounded-lg p-2 shadow-sm font-sans">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <span className="text-lg font-bold tracking-tight text-gray-900 pr-8">
                {topic.name}
              </span>
              <span className="px-2 text-sm font-semibold text-gray-900 bg-green-100 border border-green-200 rounded-full">
                {card.type}
              </span>
              -
              <span className="px-2 text-sm font-semibold text-gray-900 bg-green-100 border border-green-200 rounded-full">
                {card.subtype}
              </span>
              -
              <span className="px-2 text-sm font-semibold text-gray-900 bg-green-100 border border-green-200 rounded-full">
                {displayVariant}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => console.log("Test trigger for", card)}
                className="p-1 text-gray-900 hover:text-gray-600 focus:outline-none"
              >
                <FontAwesomeIcon icon={faPlay} />
              </button>
              <button
                onClick={() => console.log("Settings for", card)}
                className="p-1 text-gray-900 hover:text-gray-600 focus:outline-none"
              >
                <FontAwesomeIcon icon={faCog} />
              </button>
            </div>
          </div>

          <div className="flex">
            <div className="flex flex-wrap items-center text-sm text-gray-800 mt-2 divide-x divide-gray-300 w-full">
              <div className="flex items-center px-2">
                <span className="font-semibold text-gray-900">Watching:</span>
                <span className="font-medium ml-1">
                  {displayParameters.topic}
                </span>
              </div>
              <div className="flex items-center px-2">
                <button
                  onClick={() => setShowCondition(!showCondition)}
                  className="text-sm font-semibold text-blue-600 hover:underline focus:outline-none"
                >
                  Condition
                </button>
              </div>
              <div className="flex items-center px-2">
                <span className="font-semibold text-gray-900">Run:</span>
                <span className="font-medium text-green-600 ml-1">
                  {displayParameters.action}
                </span>
              </div>
            </div>

            <div className="text-xs font-medium text-gray-800 mt-2 w-full text-right">
              <div className="bg-green-100 border border-green-200 px-2 rounded-lg inline-block mr-2">
                Runs: {runCount}
              </div>
              <div className="bg-green-100 border border-green-200 px-2 rounded-lg inline-block mr-2">
                Last: {lastRun}
              </div>
            </div>
          </div>

          {showCondition && (
            <div className="mt-2">
              <pre className="bg-gray-100 rounded p-2 text-xs font-mono text-gray-800 mt-1 overflow-auto">
                {displayVariant === "match"
                  ? displayParameters.pattern
                  : displayParameters.script}
              </pre>
            </div>
          )}
        </div>
      );
    };

    return (
      <CardConditionalHighlight card={card}>
        {pipeline && renderPipelineView()}
        {!pipeline && renderContent()}
      </CardConditionalHighlight>
    );
  }
);

MessageEventCard.variants = ["match", "script"];
MessageEventCard.variantParameters = {
  match: {
    topic: {
      label: "Topic",
      type: "option",
      options: "topics",
    },
    action: {
      label: "Action",
      type: "option",
      options: "topics",
    },
    pattern: {
      label: "Message to match",
      type: "string",
      default: "",
    },
  },

  script: {
    topic: {
      label: "Topic",
      type: "option",
      options: "topics",
    },
    action: {
      label: "Action",
      type: "option",
      options: "topics",
    },
    script: {
      label: "Script",
      type: "code",
      default: "",
    },
  },
};

export default MessageEventCard;
